import { useQuery } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';
import { memo, useEffect, useRef, useState } from 'react';

import { Box, Grid, Line, ProgressLine, Typography } from '@core/component';
import { Translate } from '@core/constant';
import {
  useContextHeaderHeight,
  useContextMediaQuery,
  useContextProfile,
  useContextUser,
} from '@core/context';
import { getSanityQuery } from '@core/sanity';
import { ViewCMSProps, EActionAttribute, EFontWeight, ETypographyVariant } from '@core/type';
import {
  SanityBlogArticle,
  BlogArticleQueryResult,
  BlogArticlesQueryResult,
} from '@core/type/sanity';
import { MiniBannerSpotlight, SpotlightBanner } from '@core/widget';

import { BlogArticle } from './BlogArticle';
import { BlogSocialLinks } from './BlogSocialLinks';
import { BlogArticleContent } from './components/BlogArticleContent';
import { BlogArticleHead } from './components/BlogArticleHead';
import { BlogArticleImage } from './components/BlogArticleImage';

const FIXED_BLOCKS_MARGIN = 58;

export const ViewBlogArticle = memo(
  ({
    cms,
    ...rest
  }: ViewCMSProps<SanityBlogArticle> & {
    suggestedArticles: BlogArticleQueryResult['articles'];
  }) => {
    const {
      title,
      subtitle,
      body: { image },
    } = cms;
    const { t: tBlog } = useTranslation(Translate.page.BLOG_ARTICLE);

    const [isSubscriptionSuccessful, setIsSubscriptionSuccessful] = useState(false);
    const [headHeight, setHeadHeight] = useState<number>(0);
    const [imageHeight, setImageHeight] = useState<number>(0);

    const { data: suggestedArticles = [] } = useQuery({
      ...getSanityQuery<BlogArticlesQueryResult>('blogArticlesQuery', {
        ids: rest.suggestedArticles.map((a) => a._id),
      }),
    });

    const { hasNewsletterSubscription } = useContextProfile();
    const { isPhone, isDesktop } = useContextMediaQuery();
    const { height } = useContextHeaderHeight();
    const { isLogged } = useContextUser();

    const hasSubscription = isLogged && hasNewsletterSubscription;

    const headerHeight = parseInt(height) + FIXED_BLOCKS_MARGIN;

    const articleRef = useRef<HTMLDivElement>(null);
    const headRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const margin = isPhone ? 32 : 40;

    useEffect(() => {
      setHeadHeight(headRef.current.offsetHeight);
      setImageHeight(imageRef.current.offsetHeight);
    }, []);

    return (
      <>
        <ProgressLine blockRef={articleRef} />

        <Box ref={articleRef} position={'relative'} marginTop={isPhone ? '12px' : '24px'}>
          <Grid isContainer columnSpacing={30}>
            <Grid lg={2}>
              {isDesktop && (
                <Box height="100%" paddingTop={headHeight + margin}>
                  <Box position={'sticky'} top={`${headerHeight}px`} transition={'0.7s'}>
                    <Box
                      display={'flex'}
                      alignItems={'center'}
                      gap={'24px'}
                      flexDirection={'column'}
                      width={'100%'}
                    >
                      <BlogSocialLinks title={title} subtitle={subtitle} />
                    </Box>
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid xs={12} lg={7} xl={8}>
              <Box
                ref={headRef}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                marginBottom={margin}
              >
                {isPhone && (
                  <Box ref={imageRef} width={'100%'} marginBottom={margin}>
                    <BlogArticleImage image={image} title={title} />
                  </Box>
                )}

                <BlogArticleHead {...cms} />
              </Box>

              {!isPhone && (
                <Box ref={imageRef} width={'100%'} marginBottom={margin}>
                  <BlogArticleImage image={image} title={title} />
                </Box>
              )}

              <BlogArticleContent {...cms} pageId={cms._id} />
            </Grid>

            <Grid lg={3} xl={2}>
              {!isSubscriptionSuccessful &&
                isDesktop &&
                (hasSubscription ? null : (
                  <Box height="100%" paddingTop={headHeight + margin + imageHeight + margin}>
                    <Box position={'sticky'} top={`${headerHeight}px`} transition={'0.7s'}>
                      <MiniBannerSpotlight />
                    </Box>
                  </Box>
                ))}
            </Grid>
          </Grid>
        </Box>

        <Line padding={isPhone ? '24px 0 48px' : '64px 0'} isFullWidth />

        {suggestedArticles && suggestedArticles.length > 0 && (
          <Box paddingBottom={isPhone ? '104px' : isSubscriptionSuccessful ? '224px' : '144px'}>
            <Typography
              variant={ETypographyVariant.BODY1}
              as={EActionAttribute.H3}
              fontWeight={EFontWeight.BOLD}
              textAlign={'center'}
              marginBottom={isPhone ? '32px' : '48px'}
            >
              {tBlog('mayInterested')}
            </Typography>

            <Grid isContainer columnSpacing={30} rowSpacing={48}>
              {suggestedArticles.map((article) => (
                <Grid key={article.pageSlug?.slug.current} xs={12} md={6} lg={4}>
                  <BlogArticle article={article} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {!isSubscriptionSuccessful && <SpotlightBanner fn={setIsSubscriptionSuccessful} />}
      </>
    );
  },
);

ViewBlogArticle.displayName = 'ViewBlogArticle';
